import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { SERVER_URL } from '../../DB_Url.js';
import { getOrderedShapesFromPageData } from '../Replan/utils/shapePersistence';
import { METRIC_SCALES, IMPERIAL_SCALES } from '../Replan/constants/scales';
import { getShapeMeasurementData } from '../Replan/utils/measurementUtils';
import { TOOLS } from '../Replan/constants/drawingTools';

const MAX_SHAPES_PER_VERSION = 500;

export const useShapesAsTasks = (firebase, organization, projectName) => {
  const [versions, setVersions] = useState([]);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [shapesByVersion, setShapesByVersion] = useState(new Map());
  const [loadingShapes, setLoadingShapes] = useState(false);
  const [error, setError] = useState(null);
  const loadedVersionsRef = useRef(new Set());

  const fetchProjectVersions = useCallback(async () => {
    if (!projectName || !firebase) return;

    setLoadingShapes(true);
    setError(null);

    try {
      const versionsUrl = new URL(`${SERVER_URL}/api/versions`);
      versionsUrl.searchParams.append('project', projectName);
      
      const response = await fetch(versionsUrl);
      if (!response.ok) throw new Error('Failed to fetch versions');
      
      const versionNames = await response.json();
      
      if (versionNames.length === 0) {
        setVersions([]);
        setShapesByVersion(new Map());
        setSelectedVersions([]);
        loadedVersionsRef.current.clear();
        setLoadingShapes(false);
        return;
      }

      loadedVersionsRef.current.clear();
      setVersions(versionNames);

      // Persistence logic
      const storageKey = `replan_gantt_selected_versions_${projectName}`;
      const saved = localStorage.getItem(storageKey);
      
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            // Filter to ensure we only select versions that still exist
            const valid = parsed.filter(v => versionNames.includes(v));
            setSelectedVersions(valid);
          } else {
            setSelectedVersions(versionNames);
          }
        } catch (e) {
          setSelectedVersions(versionNames);
        }
      } else {
        // Default: Select all versions
        setSelectedVersions(versionNames);
      }
    } catch (err) {
      console.error('Error fetching project versions:', err);
      setError(err.message);
      setVersions([]);
    } finally {
      setLoadingShapes(false);
    }
  }, [firebase, projectName]);

  const setSelectedVersionsPersisted = useCallback((next) => {
    setSelectedVersions(next);
    if (projectName) {
      const storageKey = `replan_gantt_selected_versions_${projectName}`;
      localStorage.setItem(storageKey, JSON.stringify(next));
    }
  }, [projectName]);

  const fetchVersionShapes = useCallback(async (versionName) => {
    if (!projectName || !versionName || !firebase || !organization) return;

    if (loadedVersionsRef.current.has(versionName)) {
      return;
    }

    setLoadingShapes(true);

    try {
      const infoUrl = new URL(`${SERVER_URL}/api/document/info`);
      infoUrl.searchParams.append('project', projectName);
      
      const infoResponse = await fetch(infoUrl);
      if (!infoResponse.ok) throw new Error('Failed to fetch page info');
      
      const { pages } = await infoResponse.json();
      
      const pagesRef = firebase.fsdb
        .collection(organization)
        .doc(projectName)
        .collection('versions')
        .doc(versionName)
        .collection('pages');
      
      const pagesSnapshot = await pagesRef.get();
      
      if (pagesSnapshot.empty) {
        setShapesByVersion(prev => {
          const newMap = new Map(prev);
          newMap.set(versionName, []);
          return newMap;
        });
        setLoadingShapes(false);
        return;
      }

      const versionShapes = pagesSnapshot.docs.flatMap(pageDoc => {
        if (!pageDoc.exists) return [];

        const pageData = pageDoc.data();
        const pageIndex = parseInt(pageDoc.id, 10);
        
        const pageInfo = pages.find(p => p.version === versionName && p.pageIndex === pageIndex);
        const pageName = pageInfo?.pageLabel || `Page ${pageIndex}`;
        const pageDpi = pageInfo?.dpi || pageData.dpi || 72;
        
        const pageWidth = pageData.referenceWidth || pageData.width || 0;
        const pageHeight = pageData.referenceHeight || pageData.height || 0;
        
        const scaleKey = pageData.selectedScaleKey;
        const measurementSystem = pageData.measurementSystem || 'metric';
        const scaleList = measurementSystem === 'metric' ? METRIC_SCALES : IMPERIAL_SCALES;
        const scaleProps = scaleList.find(s => s.key === scaleKey) || scaleList[0];

        const pageShapes = getOrderedShapesFromPageData(pageData);

        return pageShapes.map(shape => {
          const shapeDpi = shape?.storedDPI || shape?.originalDPI || pageDpi;
          const ctx = {
            currentScaleProps: scaleProps,
            dpi: shapeDpi,
            imageSize: { width: pageWidth, height: pageHeight },
            displayUnitSystem: pageData.displayUnitSystem || measurementSystem,
          };

          return {
            ...shape,
            measurementContext: ctx,
            pageInfo: {
              name: pageName,
              version: versionName,
              pageIndex: pageIndex,
              organization: organization
            }
          };
        });
      });

      setShapesByVersion(prev => {
        const newMap = new Map(prev);
        newMap.set(versionName, versionShapes);
        return newMap;
      });
      loadedVersionsRef.current.add(versionName);
    } catch (err) {
      console.error(`Error fetching shapes for version ${versionName}:`, err);
    } finally {
      setLoadingShapes(false);
    }
  }, [firebase, projectName, organization]);

  useEffect(() => {
    if (projectName) {
      fetchProjectVersions();
    }
  }, [projectName, fetchProjectVersions]);

  useEffect(() => {
    selectedVersions.forEach(v => {
      fetchVersionShapes(v);
    });
  }, [selectedVersions, fetchVersionShapes]);

  const currentVersionShapes = useMemo(() => {
    const allShapes = [];
    selectedVersions.forEach(v => {
      allShapes.push(...(shapesByVersion.get(v) || []));
    });
    return allShapes;
  }, [selectedVersions, shapesByVersion]);

  return {
    versions,
    selectedVersions,
    setSelectedVersions: setSelectedVersionsPersisted,
    shapes: currentVersionShapes,
    loadingShapes,
    error
  };
};
